import { Alert, Badge, Button, Card, Input, useToast } from "@abbiamo/ui";
import {
	PortalUsersService,
	toManagedUser,
} from "@seller-hermes/features/portal/portal-users.api";
import { resolveErrorMessage } from "@seller-hermes/features/portal/portal-users-errors";
import {
	AccountSellerGroupRow,
	AllowedLinkType,
	PortalManagedUser,
	PortalManagedUserLink,
	PortalSeller,
} from "@seller-hermes/features/portal/portal-users.types";
import { PortalSellerPicker } from "./portal-seller-picker";
import {
	canGrantType,
	grantableTypes,
} from "@seller-hermes/features/portal/portal-user-types";
import {
	PortalProfile,
	PortalUserType,
} from "@seller-hermes/features/portal/portal.types";
import {
	ArrowLeft,
	Check,
	ChevronLeft,
	ChevronRight,
	Eye,
	EyeOff,
	KeyRound,
	Loader2,
	LogOut,
	Pencil,
	Plus,
	Search,
	Trash2,
	UserCheck,
	UserPlus,
	UserX,
	X,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQueries, useQuery, useQueryClient } from "react-query";
import { Link } from "react-router-dom";

const LINK_TYPES: Array<"ADMIN" | "USER"> = ["ADMIN", "USER"];

const USER_TYPE_LABEL: Record<string, string> = {
	MASTER: "Proprietário",
	ADMIN: "Administrador",
	USER: "Usuário",
	INTERNAL: "Interno",
};

const ROLE_LABEL: Record<string, string> = {
	"read-write": "Leitura/Escrita",
	read: "Leitura",
	"test-driver": "Motorista Teste",
};

const formatRoleName = (name: string) => ROLE_LABEL[name] ?? name;

type DsProduct = "group" | "go" | "pay";

const profileProduct: Record<PortalProfile, DsProduct> = {
	LOG: "group",
	GO: "go",
	PAY: "pay",
};

export const PortalPill = ({
	value,
	type = "operation",
	children = value,
}: {
	value: PortalProfile | PortalUserType;
	type?: "operation" | "type";
	children?: string;
}) => {
	if (type === "type") {
		const typeClasses: Record<string, string> = {
			MASTER: "bg-ab-go-muted text-ab-fg border border-ab-go-light",
			ADMIN: "bg-ab-ai-muted text-ab-fg border border-ab-ai-light",
			USER: "bg-ab-muted text-ab-fg-muted border border-ab-border",
			INTERNAL: "bg-ab-muted text-ab-fg-muted border border-ab-border",
		};
		return (
			<span
				className={`inline-block rounded-ab-full px-ab-2 py-0.5 text-[11px] font-ab-medium ${typeClasses[value] ?? typeClasses["USER"]}`}
			>
				{children}
			</span>
		);
	}

	const product = profileProduct[value as PortalProfile];
	if (product) {
		return (
			<Badge variant={`${product}-soft` as "group-soft" | "go-soft" | "pay-soft"}>
				{children}
			</Badge>
		);
	}
	return <Badge variant="outline">{children}</Badge>;
};

export const PortalUsersHeader = ({
	accountName,
}: {
	accountName?: string;
}) => (
	<header className="mb-ab-8 flex items-center gap-ab-3">
		<img
			src="/logos/abbiamo_icon.png"
			alt=""
			className="h-10 w-10 flex-shrink-0 object-contain"
		/>
		<div className="min-w-0">
			<p className="truncate text-ab-base text-ab-fg-muted">
				{accountName || "Conta Abbiamo"}
			</p>
			<h1 className="text-ab-xl font-ab-semibold text-ab-fg">
				Gestão de usuários
			</h1>
		</div>
		<div className="-mr-ab-3 ml-auto flex flex-shrink-0 items-center gap-ab-1">
			<Link
				to="/?select_context=true"
				data-ui="nav-link"
			>
				<ArrowLeft size={16} aria-hidden />
				Voltar
			</Link>
			<Link
				to="/logout"
				data-ui="nav-link"
			>
				<LogOut size={16} aria-hidden />
				Sair
			</Link>
		</div>
	</header>
);

export const PortalUsersStats = ({
	total,
	activeCount,
	inactiveCount,
	operations,
	isLoading,
}: {
	total: number;
	activeCount: number;
	inactiveCount: number;
	operations: PortalProfile[];
	isLoading?: boolean;
}) => {
	const valueClass = `text-ab-2xl font-ab-semibold tabular-nums text-ab-fg${isLoading ? " opacity-45" : ""}`;

	return (
		<div className="mb-ab-6 grid grid-cols-2 gap-ab-4 sm:grid-cols-4">
			<Card className="p-ab-4">
				<div className={valueClass}>{total}</div>
				<div className="mt-ab-1 text-ab-xs text-ab-fg-muted">
					Total de usuários
				</div>
			</Card>
			<Card className="p-ab-4">
				<div className={valueClass}>{activeCount}</div>
				<div className="mt-ab-1 text-ab-xs text-ab-fg-muted">
					Usuários ativos
				</div>
			</Card>
			<Card className="p-ab-4">
				<div className={valueClass}>{inactiveCount}</div>
				<div className="mt-ab-1 text-ab-xs text-ab-fg-muted">Inativos</div>
			</Card>
			<Card className="p-ab-4">
				<div className={`flex flex-wrap items-center gap-ab-1.5 ${isLoading ? "opacity-45" : ""}`}>
					<span className="text-ab-2xl font-ab-semibold tabular-nums text-ab-fg">
						{operations.length}
					</span>
					{operations.map((op) => (
						<PortalPill key={op} value={op} />
					))}
				</div>
				<div className="mt-ab-1 text-ab-xs text-ab-fg-muted">Operações</div>
			</Card>
		</div>
	);
};

export const PortalUsersListLoading = () => (
	<div
		className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-ab-2 bg-ab-card/70 text-ab-sm text-ab-fg-muted"
		role="status"
		aria-live="polite"
	>
		<Loader2 size={24} className="animate-spin" aria-hidden />
		<span>Carregando…</span>
	</div>
);

const PAGINATION_WINDOW = 3;

export const PortalUsersPagination = ({
	page,
	pageSize,
	total,
	isLoading,
	onPageChange,
}: {
	page: number;
	pageSize: number;
	total: number;
	isLoading?: boolean;
	onPageChange: (page: number) => void;
}) => {
	const totalPages = Math.max(1, Math.ceil(total / pageSize));
	const from = total === 0 ? 0 : page * pageSize + 1;
	const to = Math.min(total, (page + 1) * pageSize);

	const visiblePages = useMemo(() => {
		const start = Math.max(0, page - PAGINATION_WINDOW);
		const end = Math.min(totalPages - 1, page + PAGINATION_WINDOW);
		const pages: number[] = [];
		for (let index = start; index <= end; index += 1) {
			pages.push(index);
		}
		return pages;
	}, [page, totalPages]);

	const goToPage = (nextPage: number) => {
		if (isLoading) return;
		onPageChange(Math.max(0, Math.min(totalPages - 1, nextPage)));
	};

	const navBtn =
		"inline-flex h-8 w-8 items-center justify-center rounded-full text-ab-fg-muted transition-colors hover:bg-ab-muted hover:text-ab-fg disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent";

	return (
		<div className="flex items-center justify-between gap-ab-3 border-t border-ab-border px-ab-4 py-ab-3 text-ab-sm text-ab-fg-muted">
			<span className="whitespace-nowrap tabular-nums">
				{from}–{to} de {total}
			</span>
			<div className="flex items-center gap-ab-1.5">
				<button
					type="button"
					className={navBtn}
					aria-label="Primeira página"
					disabled={page <= 0 || isLoading}
					onClick={() => goToPage(0)}
				>
					<ChevronLeft size={14} aria-hidden />
					<ChevronLeft size={14} aria-hidden className="-ml-2.5" />
				</button>
				<button
					type="button"
					className={navBtn}
					aria-label="Página anterior"
					disabled={page <= 0 || isLoading}
					onClick={() => goToPage(page - 1)}
				>
					<ChevronLeft size={14} aria-hidden />
				</button>

				<div className="flex items-center gap-ab-1" role="group" aria-label="Páginas">
					{visiblePages.map((pageIndex) => (
						<button
							key={pageIndex}
							type="button"
							data-ui="page-button"
							className={`inline-flex h-8 min-w-[2rem] items-center justify-center px-ab-2 text-ab-sm tabular-nums transition-colors disabled:cursor-default ${
								pageIndex === page
									? "font-ab-semibold"
									: "text-ab-fg-muted hover:bg-ab-muted hover:text-ab-fg disabled:opacity-40"
							}`}
							aria-label={`Página ${pageIndex + 1}`}
							aria-current={pageIndex === page ? "page" : undefined}
							disabled={isLoading || pageIndex === page}
							onClick={() => goToPage(pageIndex)}
						>
							{pageIndex + 1}
						</button>
					))}
				</div>

				<button
					type="button"
					className={navBtn}
					aria-label="Próxima página"
					disabled={page + 1 >= totalPages || isLoading}
					onClick={() => goToPage(page + 1)}
				>
					<ChevronRight size={14} aria-hidden />
				</button>
				<button
					type="button"
					className={navBtn}
					aria-label="Última página"
					disabled={page >= totalPages - 1 || isLoading}
					onClick={() => goToPage(totalPages - 1)}
				>
					<ChevronRight size={14} aria-hidden />
					<ChevronRight size={14} aria-hidden className="-ml-2.5" />
				</button>
			</div>
		</div>
	);
};

export const PortalUsersToolbar = ({
	search,
	onSearchChange,
	operations,
	showAllOperations = true,
	activeOperation,
	onOperationChange,
	typeFilter,
	onTypeFilterChange,
	onInvite,
	canInvite = true,
}: {
	search: string;
	onSearchChange: (value: string) => void;
	operations: PortalProfile[];
	showAllOperations?: boolean;
	activeOperation: string;
	onOperationChange: (operation: string) => void;
	typeFilter: string;
	onTypeFilterChange: (value: string) => void;
	onInvite: () => void;
	canInvite?: boolean;
}) => (
	<div className="flex flex-wrap items-center gap-ab-3 border-b border-ab-border px-ab-4 py-ab-3">
		<label className="relative flex items-center">
			{/* left-ab-3 e não ab-2.5: a escala do DS não tem 2.5 (vai de 2 para
			    3), então a classe não gerava `left` e o ícone caía colado na
			    borda. 12px alinha com o px-ab-3 padrão dos controles. */}
			<Search
				size={15}
				className="pointer-events-none absolute left-ab-3 text-ab-fg-muted"
				aria-hidden
			/>
			<Input
				aria-label="Buscar por email"
				placeholder="Buscar por email"
				value={search}
				onChange={(event) => onSearchChange(event.target.value)}
				className="h-ab-ctl w-64 pl-ab-8"
			/>
		</label>

		<select
			aria-label="Selecionar operação"
			value={activeOperation}
			onChange={(event) => onOperationChange(event.target.value)}
			className="h-ab-ctl rounded-ab border border-ab-border bg-ab-card px-ab-3 text-ab-sm text-ab-fg outline-none focus:ring-2 focus:ring-ab-ring focus:ring-offset-1"
		>
			{showAllOperations ? (
				<option value="ALL">Todas as operações</option>
			) : null}
			{operations.map((operation) => (
				<option key={operation} value={operation}>
					{operation}
				</option>
			))}
		</select>

		<select
			aria-label="Filtrar tipos"
			value={typeFilter}
			onChange={(event) => onTypeFilterChange(event.target.value)}
			className="h-ab-ctl rounded-ab border border-ab-border bg-ab-card px-ab-3 text-ab-sm text-ab-fg outline-none focus:ring-2 focus:ring-ab-ring focus:ring-offset-1"
		>
			<option value="">Todos os tipos</option>
			<option value="MASTER">Proprietário</option>
			<option value="ADMIN">Administrador</option>
			<option value="USER">Usuário</option>
		</select>

		<div className="ml-auto">
			<Button size="sm" onClick={onInvite} disabled={!canInvite}>
				<UserPlus size={15} aria-hidden />
				Convidar usuário
			</Button>
		</div>
	</div>
);

export const PortalUserLinks = ({
	links,
	disabled,
}: {
	links: PortalManagedUserLink[];
	disabled?: boolean;
}) => (
	<div className={`flex flex-col gap-1 ${disabled ? "opacity-50" : ""}`}>
		{links.map((link) => (
			<span key={link.id} className="inline-flex items-center gap-ab-1">
				<PortalPill value={link.operationProfile} />
				<span className="text-[10px] text-ab-fg-muted">
					{USER_TYPE_LABEL[link.type] ?? link.type}
				</span>
			</span>
		))}
	</div>
);

export const PortalActionMenu = ({
	user,
	index,
	openMenu,
	onToggle,
	onOpenDetail,
	canChangePassword,
	onChangePassword,
	onDeactivate,
	onReactivate,
	isBusy,
}: {
	user: PortalManagedUser;
	index: number;
	openMenu: number | null;
	onToggle: (index: number) => void;
	onOpenDetail: (user: PortalManagedUser) => void;
	canChangePassword: boolean;
	onChangePassword: (user: PortalManagedUser) => void;
	onDeactivate: (user: PortalManagedUser) => void;
	onReactivate: (user: PortalManagedUser) => void;
	isBusy: boolean;
}) => {
	const isOpen = openMenu === index;

	return (
		<td className="relative w-12 pr-ab-2">
			<button
				type="button"
				className="flex h-8 w-8 items-center justify-center text-ab-fg-muted"
				data-ui="icon-button"
				aria-label={`Abrir ações de ${user.name}`}
				onClick={(event) => {
					event.stopPropagation();
					onToggle(index);
				}}
			>
				⋯
			</button>
			{isOpen ? (
				<div
					className="absolute right-ab-2 top-10 z-30 w-48"
					data-ui="menu"
				>
					<button
						type="button"
						className="flex w-full items-center gap-ab-2 px-ab-3 py-ab-1.5 text-ab-sm text-ab-fg hover:bg-ab-muted"
						onClick={(event) => {
							event.stopPropagation();
							onOpenDetail(user);
						}}
					>
						<Eye size={12} aria-hidden />
						Ver detalhes
					</button>
					{canChangePassword ? (
						<button
							type="button"
							className="flex w-full items-center gap-ab-2 px-ab-3 py-ab-1.5 text-ab-sm text-ab-fg hover:bg-ab-muted"
							onClick={(event) => {
								event.stopPropagation();
								onChangePassword(user);
							}}
						>
							<KeyRound size={12} aria-hidden />
							Alterar senha
						</button>
					) : null}
					{user.status === "Inativo" ? (
						<button
							type="button"
							className="flex w-full items-center gap-ab-2 px-ab-3 py-ab-1.5 text-ab-sm text-eui-accent hover:bg-ab-muted disabled:opacity-40"
							disabled={isBusy}
							onClick={(event) => {
								event.stopPropagation();
								onReactivate(user);
							}}
						>
							<UserCheck size={12} aria-hidden />
							Reativar
						</button>
					) : (
						<>
							<div className="my-1 border-t border-ab-border" />
							<button
								type="button"
								className="flex w-full items-center gap-ab-2 px-ab-3 py-ab-1.5 text-ab-sm text-ab-destructive hover:bg-ab-destructive/5 disabled:opacity-40"
								disabled={isBusy}
								onClick={(event) => {
									event.stopPropagation();
									onDeactivate(user);
								}}
							>
								<UserX size={12} aria-hidden />
								Desativar
							</button>
						</>
					)}
				</div>
			) : null}
		</td>
	);
};

const StatusBadge = ({ status }: { status: string }) => (
	<span
		className={`inline-flex items-center rounded-full px-ab-2 py-0.5 text-[11px] font-ab-medium ${
			status === "Ativo"
				? "bg-eui-accent-tint text-eui-accent"
				: "bg-ab-muted text-ab-fg-muted"
		}`}
	>
		{status}
	</span>
);

export const PortalUsersTable = ({
	users,
	activeOperation,
	selectedMenu,
	onToggleMenu,
	onOpenDetail,
	canChangePassword,
	onChangePassword,
	onDeactivate,
	onReactivate,
	busyUserId,
}: {
	users: PortalManagedUser[];
	activeOperation: string;
	selectedMenu: number | null;
	onToggleMenu: (index: number) => void;
	onOpenDetail: (user: PortalManagedUser) => void;
	canChangePassword: (user: PortalManagedUser) => boolean;
	onChangePassword: (user: PortalManagedUser) => void;
	onDeactivate: (user: PortalManagedUser) => void;
	onReactivate: (user: PortalManagedUser) => void;
	busyUserId: string | null;
}) => (
	<table className="w-full border-collapse text-ab-sm text-ab-fg" data-ui="users-table">
		<thead className="border-b border-ab-border bg-eui-row-hover text-ab-xs text-ab-fg-muted">
			<tr>
				<th className="px-ab-4 py-ab-2.5 text-left font-ab-medium">Email</th>
				<th className="px-ab-4 py-ab-2.5 text-left font-ab-medium">Vínculos</th>
				<th className="px-ab-4 py-ab-2.5 text-left font-ab-medium">Status</th>
				<th className="px-ab-4 py-ab-2.5 text-left font-ab-medium">Último acesso</th>
				<th />
			</tr>
		</thead>
		<tbody>
			{users.map((user, index) => {
				const isInactive = user.status !== "Ativo";
				const operationLinks = activeOperation
					? user.links.filter(
							(link) => link.operationProfile === activeOperation,
						)
					: user.links;

				return (
					<tr
						key={user.id}
						className="cursor-pointer border-b border-ab-border transition-colors last:border-b-0 hover:bg-eui-row-hover"
						onClick={() => onOpenDetail(user)}
					>
						<td className="px-ab-4 py-ab-3 font-ab-medium text-ab-fg-muted">
							{user.email}
						</td>
						<td className="px-ab-4 py-ab-3">
							<PortalUserLinks links={operationLinks} disabled={isInactive} />
						</td>
						<td className="px-ab-4 py-ab-3">
							<StatusBadge status={user.status} />
						</td>
						<td className="px-ab-4 py-ab-3 text-ab-fg-muted">{user.lastAccess}</td>
						<PortalActionMenu
							user={user}
							index={index}
							openMenu={selectedMenu}
							onToggle={onToggleMenu}
							onOpenDetail={onOpenDetail}
							canChangePassword={canChangePassword(user)}
							onChangePassword={onChangePassword}
							onDeactivate={onDeactivate}
							onReactivate={onReactivate}
							isBusy={busyUserId === user.id}
						/>
					</tr>
				);
			})}
		</tbody>
	</table>
);

// ---------------------------------------------------------------------------
// Shared modal overlay
// ---------------------------------------------------------------------------

const ModalOverlay = ({
	isActive,
	onClick,
	zIndex = "z-50",
	children,
}: {
	isActive: boolean;
	onClick?: () => void;
	zIndex?: string;
	children?: React.ReactNode;
}) => (
	// Os cards limitam a própria altura (max-h + rolagem interna), então nunca
	// excedem o overlay e a centralização é segura. O overflow aqui é rede de
	// proteção para viewports muito baixas.
	<div
		data-ui="overlay"
			className={`fixed inset-0 ${zIndex} ab-scroll flex items-center justify-center overflow-y-auto p-ab-4 transition-opacity ${
			isActive ? "opacity-100" : "pointer-events-none opacity-0"
		}`}
		onClick={onClick}
	>
		{children}
	</div>
);

// ---------------------------------------------------------------------------
// Confirm dialog
// ---------------------------------------------------------------------------

export const PortalConfirmDialog = ({
	isOpen,
	title,
	description,
	confirmLabel,
	onConfirm,
	onCancel,
	isLoading = false,
}: {
	isOpen: boolean;
	title: string;
	description: string;
	confirmLabel: string;
	onConfirm: () => void;
	onCancel: () => void;
	isLoading?: boolean;
}) => (
	<ModalOverlay isActive={isOpen} onClick={onCancel}>
		<Card
			className="w-full max-w-sm p-ab-6"
			onClick={(e) => e.stopPropagation()}
		>
			<h3 className="text-ab-lg font-ab-semibold text-ab-fg">{title}</h3>
			<p className="mt-ab-2 text-ab-sm text-ab-fg-muted">{description}</p>
			<div className="mt-ab-6 flex justify-end gap-ab-2">
				<Button variant="outline" size="sm" onClick={onCancel} disabled={isLoading}>
					Cancelar
				</Button>
				<Button
					variant="destructive"
					size="sm"
					onClick={onConfirm}
					disabled={isLoading}
					loading={isLoading}
				>
					{confirmLabel}
				</Button>
			</div>
		</Card>
	</ModalOverlay>
);

// ---------------------------------------------------------------------------
// Change password modal
// ---------------------------------------------------------------------------

// Mesma regra do backend (PASSWORD_COMPLEXITY_REGEX no dashboard-api) e do
// Cognito; o checklist é a soma destes testes, então validar aqui = validar lá.
const PASSWORD_RULES: Array<{ label: string; test: (value: string) => boolean }> = [
	{ label: "Pelo menos 8 caracteres", test: (value) => value.length >= 8 },
	{ label: "Uma letra maiúscula", test: (value) => /[A-Z]/.test(value) },
	{ label: "Uma letra minúscula", test: (value) => /[a-z]/.test(value) },
	{ label: "Um número", test: (value) => /[0-9]/.test(value) },
	{
		label: "Um caractere especial (#?!@$%^&*-)",
		test: (value) => /[#?!@$%^&*-]/.test(value),
	},
];

export const PortalChangePasswordModal = ({
	user,
	sellerGroupId,
	onClose,
}: {
	user: PortalManagedUser;
	// Operação compartilhada que habilita o requester (changePasswordContext).
	// Vai no header de contexto; a autorização real é pelos vínculos, no backend.
	sellerGroupId?: string;
	onClose: () => void;
}) => {
	const { success } = useToast();
	const [newPassword, setNewPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const passwordValid = PASSWORD_RULES.every((rule) => rule.test(newPassword));
	const confirmMismatch =
		confirmPassword.length > 0 && confirmPassword !== newPassword;
	const canSubmit =
		passwordValid && confirmPassword.length > 0 && !confirmMismatch;

	const saveMutation = useMutation(
		() =>
			PortalUsersService.changeUserPassword(
				user.id,
				newPassword,
				sellerGroupId,
			),
		{
			onSuccess: () => {
				success("Senha alterada", `A senha de ${user.email} foi atualizada.`);
				onClose();
			},
			onError: (mutationError: unknown) =>
				setError(resolveErrorMessage(mutationError)),
		},
	);

	const submit = () => {
		if (!canSubmit || saveMutation.isLoading) return;
		saveMutation.mutate();
	};

	return (
		<ModalOverlay isActive onClick={onClose}>
			<Card
				className="flex max-h-[calc(100vh-2rem)] w-full max-w-md flex-col p-ab-6"
				role="dialog"
				aria-modal="true"
				onClick={(event) => event.stopPropagation()}
			>
				<ModalHeader title="Alterar senha" onClose={onClose} />

				<div className="ab-scroll flex min-h-0 flex-1 flex-col gap-ab-4 overflow-y-auto">
					<div className="rounded-[12px] bg-ab-muted px-ab-3 py-ab-2 text-ab-sm text-ab-fg-muted">
						{user.email}
					</div>

					<div className="grid gap-ab-1.5">
						<label
							className="text-ab-sm font-ab-medium text-ab-fg"
							htmlFor="portal-new-password"
						>
							Nova senha
						</label>
						<div className="relative">
							<Input
								id="portal-new-password"
								type={showPassword ? "text" : "password"}
								autoComplete="new-password"
								value={newPassword}
								onChange={(event) => setNewPassword(event.target.value)}
								className="pr-10"
							/>
							<button
								type="button"
								className="absolute inset-y-0.5 right-0.5 flex w-8 items-center justify-center text-ab-fg-muted"
								data-ui="icon-button"
								aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
								onClick={() => setShowPassword((current) => !current)}
							>
								{showPassword ? (
									<EyeOff size={16} aria-hidden />
								) : (
									<Eye size={16} aria-hidden />
								)}
							</button>
						</div>
					</div>

					<ul className="grid gap-ab-1 text-ab-xs">
						{PASSWORD_RULES.map((rule) => {
							const passed = rule.test(newPassword);
							return (
								<li
									key={rule.label}
									className={`flex items-center gap-ab-1.5 ${
										passed ? "text-eui-accent" : "text-ab-fg-muted"
									}`}
								>
									<Check
										size={12}
										aria-hidden
										className={passed ? "" : "opacity-30"}
									/>
									{rule.label}
								</li>
							);
						})}
					</ul>

					<div className="grid gap-ab-1.5">
						<label
							className="text-ab-sm font-ab-medium text-ab-fg"
							htmlFor="portal-confirm-password"
						>
							Confirmar nova senha
						</label>
						<Input
							id="portal-confirm-password"
							type={showPassword ? "text" : "password"}
							autoComplete="new-password"
							value={confirmPassword}
							onChange={(event) => setConfirmPassword(event.target.value)}
							onKeyDown={(event) => {
								if (event.key === "Enter") submit();
							}}
						/>
						{confirmMismatch ? (
							<p className="text-ab-xs text-ab-destructive">
								As senhas não coincidem.
							</p>
						) : null}
					</div>
				</div>

				<div className="mt-ab-4 flex flex-shrink-0 flex-col gap-ab-4">
					{error ? <Alert variant="error">{error}</Alert> : null}

					<Button
						className="w-full"
						size="lg"
						disabled={!canSubmit || saveMutation.isLoading}
						loading={saveMutation.isLoading}
						onClick={submit}
					>
						{saveMutation.isLoading ? "Alterando…" : "Alterar senha"}
					</Button>
				</div>
			</Card>
		</ModalOverlay>
	);
};

// ---------------------------------------------------------------------------
// Invite modal
// ---------------------------------------------------------------------------

interface InviteLinkState {
	included: boolean;
	type: AllowedLinkType;
	roleId: string;
	// null enquanto as filiais da operação carregam; [] é estado válido.
	sellerIds: string[] | null;
	// A lista de filiais fica recolhida por padrão: elas já vêm todas marcadas,
	// então o caso dominante nunca precisa abri-la. Expandir todas de uma vez
	// fazia o modal passar de 1000px com 3 operações marcadas.
	expanded: boolean;
}

export const PortalInviteModal = ({
	isOpen,
	onClose,
	sellerGroups,
	activeSellerGroupId,
	sellerGroupTypes = {},
}: {
	isOpen: boolean;
	onClose: () => void;
	sellerGroups: AccountSellerGroupRow[];
	activeSellerGroupId: string | null;
	// Tipo do usuário logado em cada operação, para saber o que ele pode conceder.
	sellerGroupTypes?: Record<string, PortalUserType | undefined>;
}) => {
	const queryClient = useQueryClient();
	const [email, setEmail] = useState("");
	const [links, setLinks] = useState<Record<string, InviteLinkState>>({});
	const [error, setError] = useState<string | null>(null);

	const buildInitialLinks = useMemo(() => {
		return () =>
			sellerGroups.reduce<Record<string, InviteLinkState>>(
				(accumulator, sellerGroup) => {
					accumulator[sellerGroup.id] = {
						included: sellerGroup.id === activeSellerGroupId,
						type: "USER",
						roleId: "",
						sellerIds: null,
						expanded: false,
					};
					return accumulator;
				},
				{},
			);
	}, [sellerGroups, activeSellerGroupId]);

	React.useEffect(() => {
		if (isOpen) {
			setEmail("");
			setError(null);
			setLinks(buildInitialLinks());
		}
	}, [isOpen, buildInitialLinks]);

	const selectedUserSellerGroupIds = sellerGroups
		.filter(
			(sellerGroup) =>
				links[sellerGroup.id]?.included &&
				links[sellerGroup.id]?.type === "USER",
		)
		.map((sellerGroup) => sellerGroup.id);
	const inviteRolesQuery = useQuery(
		["portal-invite-roles", selectedUserSellerGroupIds],
		async () => {
			const roles = await Promise.all(
				selectedUserSellerGroupIds.map(async (sellerGroupId) => [
					sellerGroupId,
					await PortalUsersService.searchRoles(sellerGroupId),
				] as const),
			);
			return Object.fromEntries(roles) as Record<
				string,
				{ id: string; name: string }[]
			>;
		},
		{
			enabled: isOpen && selectedUserSellerGroupIds.length > 0,
			retry: false,
			refetchOnWindowFocus: false,
		},
	);

	const includedSellerGroupIds = sellerGroups
		.filter((sellerGroup) => links[sellerGroup.id]?.included)
		.map((sellerGroup) => sellerGroup.id);

	// Uma query por operação, e não uma única chaveada pelo array de ids: com a
	// chave composta, marcar uma operação nova invalidava a query inteira e
	// TODAS as listas de filiais voltavam a "carregando" — as já configuradas
	// piscavam para o estado vazio. Por operação, cada uma resolve sozinha e
	// reabrir uma já visitada vem do cache.
	//
	// A chave é a mesma do PortalAddLinkModal, então os dois dividem cache.
	const sellerQueries = useQueries(
		includedSellerGroupIds.map((sellerGroupId) => ({
			queryKey: ["portal-grantable-sellers", sellerGroupId],
			queryFn: () => PortalUsersService.listGrantableSellers(sellerGroupId),
			enabled: isOpen,
			retry: false,
			refetchOnWindowFocus: false,
		})),
	);

	// useQueries devolve os resultados na ordem do array de entrada; indexamos
	// por sgId para não depender de posição.
	const sellersBySellerGroup = useMemo(() => {
		const map: Record<string, { sellers: PortalSeller[]; isLoading: boolean }> =
			{};
		includedSellerGroupIds.forEach((sellerGroupId, index) => {
			const query = sellerQueries[index];
			map[sellerGroupId] = {
				sellers: (query?.data as PortalSeller[] | undefined) ?? [],
				isLoading: Boolean(query?.isLoading),
			};
		});
		return map;
	}, [includedSellerGroupIds, sellerQueries]);

	// Ao carregar as filiais de uma operação, pré-marca todas — o caso dominante
	// é conceder a operação inteira. Só inicializa quem ainda está em null, para
	// não sobrescrever o que o operador desmarcou.
	React.useEffect(() => {
		setLinks((current) => {
			let changed = false;
			const next = { ...current };
			for (const [sgId, { sellers, isLoading }] of Object.entries(
				sellersBySellerGroup,
			)) {
				if (isLoading) continue;
				if (next[sgId] && next[sgId].sellerIds === null) {
					next[sgId] = {
						...next[sgId],
						sellerIds: sellers.map((seller) => seller.id),
					};
					changed = true;
				}
			}
			return changed ? next : current;
		});
	}, [sellersBySellerGroup]);

	const inviteMutation = useMutation(
		() => {
			const selectedLinks = sellerGroups
				.filter((sellerGroup) => links[sellerGroup.id]?.included)
				.map((sellerGroup) => {
					const link = links[sellerGroup.id];
					return {
						sg_id: sellerGroup.id,
						type: link.type,
						...(link.type === "USER" ? { role_id: link.roleId } : {}),
						seller_ids: link.sellerIds ?? [],
					};
				});
			return PortalUsersService.inviteUser(
				{ email: email.trim(), links: selectedLinks },
				activeSellerGroupId ?? undefined,
			);
		},
		{
			onSuccess: async () => {
				await queryClient.invalidateQueries(["portal-users"]);
				onClose();
			},
			onError: (mutationError: unknown) => {
				setError(resolveErrorMessage(mutationError));
			},
		},
	);

	if (!isOpen) return null;

	const selected = sellerGroups.filter(
		(sellerGroup) => links[sellerGroup.id]?.included,
	);
	// Filial não é obrigatória: um vínculo pode nascer sem nenhuma. O aviso
	// abaixo do picker explica a consequência sem bloquear o envio.
	const canSubmit =
		/.+@.+\..+/.test(email.trim()) &&
		selected.length > 0 &&
		selected.every((sellerGroup) => {
			const link = links[sellerGroup.id];
			return link.type !== "USER" || Boolean(link.roleId);
		});

	const updateLink = (sgId: string, patch: Partial<InviteLinkState>) =>
		setLinks((current) => ({
			...current,
			[sgId]: { ...current[sgId], ...patch },
		}));

	return (
		<ModalOverlay isActive={isOpen} onClick={onClose}>
			<Card
				className="flex max-h-[calc(100vh-2rem)] w-full max-w-lg flex-col p-ab-6"
				role="dialog"
				aria-modal="true"
				onClick={(event) => event.stopPropagation()}
			>
				<div className="flex-shrink-0">
					<ModalHeader title="Convidar usuário" onClose={onClose} />
				</div>

				{/* Só o miolo rola: cabeçalho e botão de envio ficam sempre visíveis. */}
				<div className="ab-scroll flex min-h-0 flex-1 flex-col gap-ab-4 overflow-y-auto">
					<div className="grid gap-ab-1.5">
						<label className="text-ab-sm font-ab-medium text-ab-fg">Email</label>
						<Input
							type="email"
							placeholder="novo@empresa.com"
							value={email}
							onChange={(event) => setEmail(event.target.value)}
							className="h-ab-ctl-lg"
						/>
					</div>

					<div>
						<p className="mb-ab-3 text-ab-sm font-ab-medium text-ab-fg">
							Vínculos com operações
						</p>
						<p className="mb-ab-3 text-ab-xs text-ab-fg-muted">
							Selecione as operações e o tipo de acesso para cada uma.
						</p>

						{sellerGroups.length === 0 ? (
							<p className="text-ab-sm text-ab-fg-muted">
								Nenhuma operação disponível para vínculo.
							</p>
						) : (
							<div className="flex flex-col gap-ab-2">
								{sellerGroups.map((sellerGroup) => {
									const state = links[sellerGroup.id];
									if (!state) return null;

									const sellerState = sellersBySellerGroup[sellerGroup.id] ?? {
										sellers: [],
										isLoading: false,
									};
									// Resumo no lugar da lista aberta: mantém a informação
									// visível sem custar 240px de altura por operação.
									const selectedCount = state.sellerIds?.length ?? 0;
									const totalCount = sellerState.sellers.length;
									const sellerSummary =
										selectedCount === 0
											? "nenhuma"
											: selectedCount === totalCount
												? `todas (${totalCount})`
												: `${selectedCount} de ${totalCount}`;

									return (
										<div
											key={sellerGroup.id}
											className="p-ab-3"
											data-ui="block"
										>
											<label className="flex items-center gap-ab-3">
												<input
													type="checkbox"
													className="h-4 w-4 rounded accent-ab-fg"
													checked={state.included}
													onChange={(event) =>
														updateLink(sellerGroup.id, {
															included: event.target.checked,
														})
													}
												/>
												{sellerGroup.profile ? (
													<PortalPill
														value={sellerGroup.profile as PortalProfile}
													/>
												) : null}
												<span className="text-ab-sm font-ab-medium text-ab-fg">
													{sellerGroup.name}
												</span>
											</label>
											<div
												className={`mt-ab-2 ml-7 flex gap-ab-2 transition-opacity ${
													state.included ? "opacity-100" : "pointer-events-none opacity-40"
												}`}
											>
												<select
													disabled={!state.included}
													value={state.type}
													onChange={(event) =>
														updateLink(sellerGroup.id, {
															type: event.target.value as AllowedLinkType,
															roleId: "",
														})
													}
													className="h-ab-ctl rounded-ab border border-ab-border bg-ab-card px-ab-2 text-ab-xs text-ab-fg outline-none"
												>
													{grantableTypes(sellerGroupTypes[sellerGroup.id]).map(
														(linkType) => (
															<option key={linkType} value={linkType}>
																{USER_TYPE_LABEL[linkType] ?? linkType}
															</option>
														),
													)}
												</select>
												{state.type === "USER" ? (
												<select
													disabled={!state.included}
													value={state.roleId}
													onChange={(event) =>
														updateLink(sellerGroup.id, {
															roleId: event.target.value,
														})
													}
													className="h-ab-ctl flex-1 rounded-ab border border-ab-border bg-ab-card px-ab-2 text-ab-xs text-ab-fg outline-none"
												>
													<option value="" disabled>
														Selecione uma permissão
													</option>
													{(inviteRolesQuery.data?.[sellerGroup.id] ?? []).map((role) => (
														<option key={role.id} value={role.id}>
															{formatRoleName(role.name)}
														</option>
													))}
												</select>
												) : null}
											</div>

											{state.included ? (
												<div className="mt-ab-3 ml-7">
													<button
														type="button"
														className="flex w-full items-center gap-ab-2 rounded-[12px] bg-ab-muted/60 px-ab-3 py-ab-2 text-left text-ab-xs text-ab-fg transition-colors hover:bg-ab-muted"
														aria-expanded={state.expanded}
														onClick={() =>
															updateLink(sellerGroup.id, {
																expanded: !state.expanded,
															})
														}
													>
														<ChevronRight
															size={14}
															aria-hidden
															className={`flex-shrink-0 transition-transform ${
																state.expanded ? "rotate-90" : ""
															}`}
														/>
														<span>Filiais com acesso</span>
														<span className="ml-auto text-ab-fg-muted">
															{sellerState.isLoading && state.sellerIds === null
																? "carregando…"
																: sellerSummary}
														</span>
													</button>

													{state.expanded ? (
														<div className="mt-ab-2">
															<PortalSellerPicker
																sellers={sellerState.sellers}
																selectedIds={state.sellerIds}
																onChange={(sellerIds) =>
																	updateLink(sellerGroup.id, { sellerIds })
																}
																label=""
																hint="Selecione as filiais que este usuário poderá acessar nesta operação."
															/>
														</div>
													) : null}

													{state.sellerIds?.length === 0 ? (
														<p className="mt-ab-1.5 text-ab-xs text-ab-fg-muted">
															Sem filial, o usuário não conseguirá acessar o
															dashboard desta operação até receber uma.
														</p>
													) : null}
												</div>
											) : null}
										</div>
									);
								})}
							</div>
						)}
					</div>

				</div>

				<div className="mt-ab-4 flex flex-shrink-0 flex-col gap-ab-4">
					{error ? <Alert variant="error">{error}</Alert> : null}

					<Button
						className="w-full"
						size="lg"
						disabled={!canSubmit || inviteMutation.isLoading}
						loading={inviteMutation.isLoading}
						onClick={() => inviteMutation.mutate()}
					>
						{inviteMutation.isLoading ? "Enviando…" : "Enviar convite"}
					</Button>
				</div>
			</Card>
		</ModalOverlay>
	);
};

// ---------------------------------------------------------------------------
// User drawer + per-link cards
// ---------------------------------------------------------------------------

export const PortalUserDetailCard = ({
	userId,
	link,
	onEdit,
	onRemove,
	isRemoving,
}: {
	userId: string;
	link: PortalManagedUserLink;
	onEdit: (link: PortalManagedUserLink) => void;
	onRemove: (link: PortalManagedUserLink) => void;
	isRemoving: boolean;
}) => {
	const detailQuery = useQuery(
		["portal-user-detail", userId, link.id],
		() => PortalUsersService.getUserDetail(userId, link.id),
		{ retry: false, refetchOnWindowFocus: false },
	);

	const branches = detailQuery.data?.sellers ?? [];
	const BRANCH_PREVIEW_LIMIT = 20;
	const visibleBranches = branches.slice(0, BRANCH_PREVIEW_LIMIT);
	const hiddenCount = branches.length - visibleBranches.length;

	return (
		<div className="p-ab-4" data-ui="block">
			<div className="mb-ab-2 flex items-center gap-ab-2">
				<PortalPill value={link.operationProfile} />
				<span className="text-ab-sm font-ab-medium text-ab-fg">
					{link.operationName}
				</span>
				<PortalPill value={link.type} type="type">
					{USER_TYPE_LABEL[link.type] ?? link.type}
				</PortalPill>
			</div>
			<p className="mb-ab-2 text-ab-xs text-ab-fg-muted">
				Permissões: {link.role ? formatRoleName(link.role) : "—"}
			</p>
			<p className="mb-ab-1 text-ab-xs text-ab-fg-muted">Filiais com acesso:</p>
			<div className="mb-ab-3 flex flex-wrap gap-1">
				{detailQuery.isLoading ? (
					<span className="text-ab-xs text-ab-fg-muted">Carregando…</span>
				) : branches.length > 0 ? (
					<>
						{visibleBranches.map((branch) => (
							<span
								key={branch.id}
								className="rounded-full px-ab-2 py-0.5 text-[11px] bg-ab-muted text-ab-fg-muted"
							>
								{branch.trading_name}
							</span>
						))}
						{hiddenCount > 0 ? (
							<span className="rounded-full px-ab-2 py-0.5 text-[11px] bg-ab-fg text-ab-bg font-ab-medium">
								+{hiddenCount} filiais
							</span>
						) : null}
						<button
							type="button"
							className="text-[11px] ml-1 font-ab-medium text-eui-accent hover:underline"
							onClick={() => onEdit(link)}
						>
							editar
						</button>
					</>
				) : (
					<span className="text-ab-xs text-ab-fg-muted">Sem filiais</span>
				)}
			</div>
			<div className="flex items-center gap-ab-4 border-t border-ab-border pt-ab-2">
				<button
					type="button"
					className="inline-flex items-center gap-ab-1 text-ab-xs font-ab-medium text-eui-accent hover:underline"
					onClick={() => onEdit(link)}
				>
					<Pencil size={11} aria-hidden />
					Editar tipo/permissão
				</button>
				<button
					type="button"
					className="inline-flex items-center gap-ab-1 text-ab-xs text-ab-destructive hover:underline disabled:opacity-40"
					disabled={isRemoving}
					onClick={() => onRemove(link)}
				>
					{isRemoving ? (
						<Loader2 size={11} aria-hidden className="animate-spin" />
					) : (
						<Trash2 size={11} aria-hidden />
					)}
					{isRemoving ? "Removendo…" : "Remover vínculo"}
				</button>
			</div>
		</div>
	);
};

export const PortalUserDrawer = ({
	user,
	accountName,
	sellerGroups,
	activeSellerGroupId,
	sellerGroupTypes = {},
	canChangePassword = false,
	onChangePassword,
	onClose,
	onUserStatusChange,
	onLinkRemoved,
}: {
	user: PortalManagedUser | null;
	accountName?: string;
	sellerGroups: AccountSellerGroupRow[];
	activeSellerGroupId: string | null;
	// Tipo do usuário logado em cada operação — define o que ele pode gerir ali.
	sellerGroupTypes?: Record<string, PortalUserType | undefined>;
	canChangePassword?: boolean;
	onChangePassword?: (user: PortalManagedUser) => void;
	onClose: () => void;
	onUserStatusChange: (userId: string, active: boolean) => void;
	onLinkRemoved: (userId: string, sgId: string) => void;
}) => {
	type DrawerConfirm =
		| { kind: "deactivate" }
		| { kind: "remove-link"; sgId: string; operationName: string };

	const queryClient = useQueryClient();
	const [editingLink, setEditingLink] =
		useState<PortalManagedUserLink | null>(null);
	const [isAddingLink, setIsAddingLink] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [pendingConfirm, setPendingConfirm] =
		useState<DrawerConfirm | null>(null);

	const invalidateUsers = () =>
		queryClient.invalidateQueries(["portal-users"]);

	const deactivateMutation = useMutation(
		() =>
			PortalUsersService.setUserActive(
				user!.id,
				false,
				activeSellerGroupId ?? undefined,
			),
		{
			onSuccess: async () => {
				onUserStatusChange(user!.id, false);
				await invalidateUsers();
			},
			onError: (mutationError: unknown) =>
				setError(resolveErrorMessage(mutationError)),
		},
	);

	const reactivateMutation = useMutation(
		() =>
			PortalUsersService.setUserActive(
				user!.id,
				true,
				activeSellerGroupId ?? undefined,
			),
		{
			onSuccess: async () => {
				onUserStatusChange(user!.id, true);
				await invalidateUsers();
			},
			onError: (mutationError: unknown) =>
				setError(resolveErrorMessage(mutationError)),
		},
	);

	const removeLinkMutation = useMutation(
		(sgId: string) =>
			PortalUsersService.updateUserLinks(
				user!.id,
				{ remove: [sgId] },
				activeSellerGroupId ?? undefined,
			),
		{
			onSuccess: async (_data, sgId) => {
				onLinkRemoved(user!.id, sgId);
				await invalidateUsers();
			},
			onError: (mutationError: unknown) =>
				setError(resolveErrorMessage(mutationError)),
		},
	);

	const linkedSellerGroupIds = new Set(user?.links.map((link) => link.id));
	const availableSellerGroups = sellerGroups.filter(
		(sellerGroup) => !linkedSellerGroupIds.has(sellerGroup.id),
	);

	return (
		<>
			{/* Backdrop */}
			<div
				data-ui="overlay"
				className={`fixed inset-0 z-40 transition-opacity ${
					user ? "opacity-100" : "pointer-events-none opacity-0"
				}`}
				onClick={onClose}
				role="presentation"
			/>

			{/* Slide-over panel */}
			<div
				className={`portal-users-slide-over ${user ? "is-active" : ""}`}
			>
				{user ? (
					<div className="p-ab-6">
						<ModalHeader title="Detalhes do usuário" onClose={onClose} />

						<div className="mb-ab-6 flex items-center gap-ab-4 border-b border-ab-border pb-ab-6">
							<div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full bg-eui-accent text-ab-lg font-ab-semibold text-white">
								{user.initial}
							</div>
							<div>
								<div className="text-ab-lg font-ab-semibold text-ab-fg">
									{user.name}
								</div>
								<div className="text-ab-sm text-ab-fg-muted">{user.email}</div>
								<div className="mt-ab-1">
									<StatusBadge status={user.status} />
								</div>
							</div>
						</div>

						<h4 className="mb-ab-3 text-ab-sm font-ab-semibold text-ab-fg-muted">
							Vínculos com operações
						</h4>
						<div className="flex flex-col gap-ab-3">
							{user.links.map((link) => (
								<PortalUserDetailCard
									key={link.id}
									userId={user.id}
									link={link}
									onEdit={setEditingLink}
									onRemove={(target) =>
										setPendingConfirm({
											kind: "remove-link",
											sgId: target.id,
											operationName: target.operationName,
										})
									}
									isRemoving={
										removeLinkMutation.isLoading &&
										removeLinkMutation.variables === link.id
									}
								/>
							))}
						</div>

						{availableSellerGroups.length > 0 ? (
							<button
								type="button"
								className="mt-ab-3 flex w-full items-center justify-center gap-ab-2 rounded-[14px] border border-dashed border-ab-border-strong py-ab-3 text-ab-sm font-ab-medium text-ab-fg-muted transition-colors hover:border-eui-accent hover:bg-eui-accent-tint hover:text-eui-accent"
								onClick={() => setIsAddingLink(true)}
							>
								<Plus size={15} aria-hidden />
								Adicionar vínculo com outra operação
							</button>
						) : null}

						{error ? (
							<div className="mt-ab-4">
								<Alert variant="error">{error}</Alert>
							</div>
						) : null}

						{canChangePassword && onChangePassword ? (
							<div className="mt-ab-8 border-t border-ab-border pt-ab-6">
								<h4 className="mb-ab-3 text-ab-sm font-ab-semibold text-ab-fg-muted">
									Segurança
								</h4>
								<Button
									variant="outline"
									size="sm"
									onClick={() => onChangePassword(user)}
								>
									<KeyRound size={15} aria-hidden />
									Alterar senha
								</Button>
							</div>
						) : null}

						<div className="mt-ab-8 border-t border-ab-border pt-ab-6">
							<h4 className="mb-ab-3 text-ab-sm font-ab-semibold text-ab-destructive">
								Zona de perigo
							</h4>
							{user.status === "Ativo" ? (
								<Button
									variant="outline"
									size="sm"
									disabled={deactivateMutation.isLoading}
									loading={deactivateMutation.isLoading}
									className="text-ab-destructive hover:bg-ab-destructive/5"
									onClick={() => setPendingConfirm({ kind: "deactivate" })}
								>
									<UserX size={15} aria-hidden />
									{deactivateMutation.isLoading ? "Desativando…" : "Desativar usuário"}
								</Button>
							) : (
								<Button
									variant="outline"
									size="sm"
									disabled={reactivateMutation.isLoading}
									loading={reactivateMutation.isLoading}
									className="text-ab-group hover:bg-eui-accent-tint"
									onClick={() => reactivateMutation.mutate()}
								>
									<UserCheck size={15} aria-hidden />
									{reactivateMutation.isLoading ? "Reativando…" : "Reativar usuário"}
								</Button>
							)}
						</div>
					</div>
				) : null}
			</div>

			{user && editingLink ? (
				<PortalLinkEditModal
					userId={user.id}
					link={editingLink}
					requesterType={sellerGroupTypes[editingLink.id]}
					onClose={() => setEditingLink(null)}
					onSaved={async () => {
						await invalidateUsers();
						await queryClient.invalidateQueries([
							"portal-user-detail",
							user.id,
							editingLink.id,
						]);
						setEditingLink(null);
					}}
				/>
			) : null}

			{user && isAddingLink ? (
				<PortalAddLinkModal
					userId={user.id}
					sellerGroups={availableSellerGroups}
					activeSellerGroupId={activeSellerGroupId}
					onClose={() => setIsAddingLink(false)}
					onSaved={async () => {
						await invalidateUsers();
						setIsAddingLink(false);
					}}
				/>
			) : null}

			<PortalConfirmDialog
				isOpen={pendingConfirm !== null}
				title={
					pendingConfirm?.kind === "deactivate"
						? "Desativar usuário?"
						: `Remover vínculo com ${pendingConfirm?.kind === "remove-link" ? pendingConfirm.operationName : ""}?`
				}
				description={
					pendingConfirm?.kind === "deactivate"
						? `${user?.name ?? "Este usuário"} perderá acesso ao sistema. Esta ação pode ser desfeita reativando o usuário.`
						: "O usuário perderá acesso a esta operação. Esta ação não pode ser desfeita."
				}
				confirmLabel={
					pendingConfirm?.kind === "deactivate"
						? "Desativar"
						: "Remover vínculo"
				}
				isLoading={
					deactivateMutation.isLoading || removeLinkMutation.isLoading
				}
				onCancel={() => setPendingConfirm(null)}
				onConfirm={() => {
					if (pendingConfirm?.kind === "deactivate") {
						deactivateMutation.mutate(undefined, {
							onSettled: () => setPendingConfirm(null),
						});
					} else if (pendingConfirm?.kind === "remove-link") {
						removeLinkMutation.mutate(pendingConfirm.sgId, {
							onSettled: () => setPendingConfirm(null),
						});
					}
				}}
			/>
		</>
	);
};

// ---------------------------------------------------------------------------
// Edit link modal
// ---------------------------------------------------------------------------

export const PortalLinkEditModal = ({
	userId,
	link,
	requesterType,
	onClose,
	onSaved,
}: {
	userId: string;
	link: PortalManagedUserLink;
	// Tipo do usuário logado NESTA operação. Antes o gate era "é INTERNAL?",
	// o que impedia um Proprietário de gerir a própria operação. O critério
	// correto é a hierarquia de tipos, a mesma que o backend aplica.
	requesterType?: PortalUserType;
	onClose: () => void;
	onSaved: () => void | Promise<void>;
}) => {
	const sgId = link.id;
	const editableLinkTypes = grantableTypes(requesterType);
	const [type, setType] = useState<"MASTER" | "ADMIN" | "USER">(
		link.type === "MASTER" ? "MASTER" : link.type === "ADMIN" ? "ADMIN" : "USER",
	);
	const [roleId, setRoleId] = useState<string>(link.roleId ?? "");
	const [sellerIds, setSellerIds] = useState<string[] | null>(null);
	const [error, setError] = useState<string | null>(null);

	const detailQuery = useQuery(
		["portal-user-detail", userId, sgId],
		() => PortalUsersService.getUserDetail(userId, sgId),
		{
			retry: false,
			refetchOnWindowFocus: false,
			onSuccess: (detail) => {
				setType(detail.type === "ADMIN" ? "ADMIN" : "USER");
				setRoleId(detail.role_id ?? link.roleId ?? "");
				setSellerIds(detail.sellers.map((seller) => seller.id));
			},
		},
	);

	const sellersQuery = useQuery(
		["portal-grantable-sellers", sgId],
		() => PortalUsersService.listGrantableSellers(sgId),
		{ retry: false, refetchOnWindowFocus: false },
	);

	const rolesQuery = useQuery(
		["portal-roles", sgId],
		() => PortalUsersService.searchRoles(sgId),
		{ retry: false, refetchOnWindowFocus: false, enabled: type === "USER" },
	);

	useEffect(() => {
		const availableRoles = rolesQuery.data;
		if (!availableRoles) return;
		if (roleId && !availableRoles.some((role) => role.id === roleId)) {
			setRoleId("");
		}
	}, [rolesQuery.data, roleId]);

	// Só é inegerenciável o que está acima do requester na hierarquia. Um
	// Proprietário pode editar outro Proprietário da mesma operação; um ADMIN
	// não. INTERNAL só é gerenciável por INTERNAL.
	const unmanageable = !canGrantType(requesterType, link.type);

	const saveMutation = useMutation(
		() =>
			PortalUsersService.updateUserV2(
				userId,
				{
					type,
					...(type === "USER" && roleId ? { role_id: roleId } : {}),
					active: true,
					seller_ids: sellerIds ?? [],
				},
				sgId,
			),
		{
			onSuccess: async () => {
				await onSaved();
			},
			onError: (mutationError: unknown) =>
				setError(resolveErrorMessage(mutationError)),
		},
	);

	const sellers = sellersQuery.data ?? [];
	const roles = rolesQuery.data ?? [];
	const selectedSellerIds = sellerIds ?? [];

	const isLoading =
		detailQuery.isLoading ||
		sellersQuery.isLoading ||
		(type === "USER" && rolesQuery.isLoading);
	const loadError =
		detailQuery.isError ||
		sellersQuery.isError ||
		(type === "USER" && rolesQuery.isError);

	const canSubmit =
		!unmanageable &&
		selectedSellerIds.length > 0 &&
		(type !== "USER" || Boolean(roleId));
	const showSellerSelectionError =
		sellerIds !== null && selectedSellerIds.length === 0;

	return (
		<ModalOverlay isActive onClick={onClose}>
			<Card
				className="flex max-h-[calc(100vh-2rem)] w-full max-w-lg flex-col p-ab-6"
				role="dialog"
				aria-modal="true"
				onClick={(event) => event.stopPropagation()}
			>
				<div className="flex-shrink-0">
					<ModalHeader
						title={`Editar vínculo — ${link.operationName || link.operationProfile}`}
						onClose={onClose}
					/>
				</div>

				<div className="ab-scroll flex min-h-0 flex-1 flex-col gap-ab-4 overflow-y-auto">
					{unmanageable ? (
						<Alert variant="error">
							Você não tem acesso suficiente nesta operação para editar este
							vínculo.
						</Alert>
					) : isLoading ? (
						<PortalLoadingRow label="Carregando vínculo…" />
					) : loadError ? (
						<Alert variant="error">
							Não foi possível carregar os dados deste vínculo nesta operação.
						</Alert>
					) : (
						<>
							<div className="grid gap-ab-1.5">
								<label className="text-ab-sm font-ab-medium text-ab-fg">Tipo</label>
								<select
									value={type}
									onChange={(event) => {
										setType(event.target.value as "MASTER" | "ADMIN" | "USER");
										setRoleId("");
									}}
									className="h-ab-ctl-lg w-full rounded-ab border border-ab-border bg-ab-card px-ab-3 text-ab-base text-ab-fg outline-none focus:ring-2 focus:ring-ab-ring"
								>
									{editableLinkTypes.map((linkType) => (
										<option key={linkType} value={linkType}>
											{USER_TYPE_LABEL[linkType] ?? linkType}
										</option>
									))}
								</select>
							</div>

							{type === "USER" ? (
							<div className="grid gap-ab-1.5">
								<label className="text-ab-sm font-ab-medium text-ab-fg">Permissões</label>
								<select
									value={roleId}
									onChange={(event) => setRoleId(event.target.value)}
									className="h-ab-ctl-lg w-full rounded-ab border border-ab-border bg-ab-card px-ab-3 text-ab-base text-ab-fg outline-none focus:ring-2 focus:ring-ab-ring"
								>
									<option value="" disabled>
										Selecione uma permissão
									</option>
									{roles.map((role) => (
										<option key={role.id} value={role.id}>
											{formatRoleName(role.name)}
										</option>
									))}
								</select>
							</div>
							) : null}

							<PortalSellerPicker
								sellers={sellers}
								selectedIds={sellerIds}
								onChange={setSellerIds}
								footer={
									showSellerSelectionError ? (
										<p className="mt-ab-1.5 text-ab-xs text-ab-destructive">
											Selecione pelo menos uma filial para salvar.
										</p>
									) : null
								}
							/>
						</>
					)}
				</div>

				<div className="mt-ab-4 flex flex-shrink-0 flex-col gap-ab-4">
					{error ? <Alert variant="error">{error}</Alert> : null}

					<Button
						className="w-full"
						size="lg"
						disabled={!canSubmit || saveMutation.isLoading}
						loading={saveMutation.isLoading}
						onClick={() => {
							if (!canSubmit || saveMutation.isLoading) return;
							saveMutation.mutate();
						}}
					>
						{saveMutation.isLoading ? "Salvando…" : "Salvar alterações"}
					</Button>
				</div>
			</Card>
		</ModalOverlay>
	);
};

// ---------------------------------------------------------------------------
// Add link modal
// ---------------------------------------------------------------------------

export const PortalAddLinkModal = ({
	userId,
	sellerGroups,
	activeSellerGroupId,
	onClose,
	onSaved,
}: {
	userId: string;
	sellerGroups: AccountSellerGroupRow[];
	activeSellerGroupId: string | null;
	onClose: () => void;
	onSaved: () => void | Promise<void>;
}) => {
	const [sgId, setSgId] = useState<string>(sellerGroups[0]?.id ?? "");
	const [type, setType] = useState<"ADMIN" | "USER">("USER");
	const [roleId, setRoleId] = useState<string>("");
	const [sellerIds, setSellerIds] = useState<string[] | null>(null);
	const [error, setError] = useState<string | null>(null);

	React.useEffect(() => {
		setRoleId("");
		setSellerIds(null);
	}, [sgId, sellerGroups]);

	const rolesQuery = useQuery(
		["portal-roles", sgId],
		() => PortalUsersService.searchRoles(sgId),
		{
			enabled: type === "USER" && Boolean(sgId),
			retry: false,
			refetchOnWindowFocus: false,
		},
	);

	// Sem filiais o vínculo nasce inacessível: o usuário ganha a operação mas
	// GET /me devolve 403 nela. Pré-marcamos todas, como no convite.
	const sellersQuery = useQuery(
		["portal-grantable-sellers", sgId],
		() => PortalUsersService.listGrantableSellers(sgId),
		{
			enabled: Boolean(sgId),
			retry: false,
			refetchOnWindowFocus: false,
			onSuccess: (sellers) =>
				setSellerIds((current) =>
					current === null ? sellers.map((seller) => seller.id) : current,
				),
		},
	);

	const addMutation = useMutation(
		() =>
			PortalUsersService.updateUserLinks(
				userId,
				{
					add: [
						{
							sg_id: sgId,
							type,
							...(type === "USER" ? { role_id: roleId } : {}),
							seller_ids: sellerIds ?? [],
						},
					],
				},
				activeSellerGroupId ?? undefined,
			),
		{
			onSuccess: async () => {
				await onSaved();
			},
			onError: (mutationError: unknown) =>
				setError(resolveErrorMessage(mutationError)),
		},
	);

	const canSubmit = Boolean(sgId) && (type !== "USER" || Boolean(roleId));

	return (
		<ModalOverlay isActive onClick={onClose}>
			<Card
				className="flex max-h-[calc(100vh-2rem)] w-full max-w-lg flex-col p-ab-6"
				role="dialog"
				aria-modal="true"
				onClick={(event) => event.stopPropagation()}
			>
				<div className="flex-shrink-0">
					<ModalHeader title="Adicionar vínculo" onClose={onClose} />
				</div>

				<div className="ab-scroll flex min-h-0 flex-1 flex-col gap-ab-4 overflow-y-auto">
					<div className="grid gap-ab-1.5">
						<label className="text-ab-sm font-ab-medium text-ab-fg">Operação</label>
						<select
							value={sgId}
							onChange={(event) => setSgId(event.target.value)}
							className="h-ab-ctl-lg w-full rounded-ab border border-ab-border bg-ab-card px-ab-3 text-ab-base text-ab-fg outline-none focus:ring-2 focus:ring-ab-ring"
						>
							{sellerGroups.map((sellerGroup) => (
								<option key={sellerGroup.id} value={sellerGroup.id}>
									{sellerGroup.name}
									{sellerGroup.profile ? ` (${sellerGroup.profile})` : ""}
								</option>
							))}
						</select>
					</div>

					<div className="grid gap-ab-1.5">
						<label className="text-ab-sm font-ab-medium text-ab-fg">Tipo</label>
						<select
							value={type}
							onChange={(event) => {
								setType(event.target.value as "ADMIN" | "USER");
								setRoleId("");
							}}
							className="h-ab-ctl-lg w-full rounded-ab border border-ab-border bg-ab-card px-ab-3 text-ab-base text-ab-fg outline-none focus:ring-2 focus:ring-ab-ring"
						>
							{LINK_TYPES.map((linkType) => (
								<option key={linkType} value={linkType}>
									{USER_TYPE_LABEL[linkType] ?? linkType}
								</option>
							))}
						</select>
					</div>

					{type === "USER" ? (
					<div className="grid gap-ab-1.5">
						<label className="text-ab-sm font-ab-medium text-ab-fg">Permissões</label>
						<select
							value={roleId}
							onChange={(event) => setRoleId(event.target.value)}
							className="h-ab-ctl-lg w-full rounded-ab border border-ab-border bg-ab-card px-ab-3 text-ab-base text-ab-fg outline-none focus:ring-2 focus:ring-ab-ring"
						>
							<option value="" disabled>
								Selecione uma permissão
							</option>
							{(rolesQuery.data ?? []).map((role) => (
								<option key={role.id} value={role.id}>
									{formatRoleName(role.name)}
								</option>
							))}
						</select>
					</div>
					) : null}

					<PortalSellerPicker
						sellers={sellersQuery.data ?? []}
						selectedIds={sellerIds}
						onChange={setSellerIds}
						hint="Selecione as filiais que este usuário poderá acessar nesta operação."
						footer={
							sellerIds?.length === 0 ? (
								<p className="mt-ab-1.5 text-ab-xs text-ab-fg-muted">
									Sem filial, o usuário não conseguirá acessar o dashboard desta
									operação até receber uma.
								</p>
							) : null
						}
					/>

				</div>

				<div className="mt-ab-4 flex flex-shrink-0 flex-col gap-ab-4">
					{error ? <Alert variant="error">{error}</Alert> : null}

					<Button
						className="w-full"
						size="lg"
						disabled={!canSubmit || addMutation.isLoading}
						loading={addMutation.isLoading}
						onClick={() => addMutation.mutate()}
					>
						{addMutation.isLoading ? "Adicionando…" : "Adicionar vínculo"}
					</Button>
				</div>
			</Card>
		</ModalOverlay>
	);
};

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

const ModalHeader = ({
	title,
	onClose,
}: {
	title: string;
	onClose: () => void;
}) => (
	<div className="mb-ab-6 flex items-center justify-between">
		<h3 className="text-ab-lg font-ab-semibold text-ab-fg">{title}</h3>
		<button
			type="button"
			className="-mr-ab-2 flex h-8 w-8 items-center justify-center text-ab-fg-muted"
			data-ui="icon-button"
			onClick={onClose}
			aria-label="Fechar"
		>
			<X size={20} aria-hidden />
		</button>
	</div>
);

export const PortalLoadingRow = ({ label }: { label: string }) => (
	<div className="flex items-center gap-ab-1.5 text-ab-xs text-ab-fg-muted">
		<Loader2 size={13} className="animate-spin" aria-hidden />
		{label}
	</div>
);

// A tradução de erros vive em módulo próprio (sem aws-amplify/UI) para ser testável.
export { resolveErrorMessage };
